import React, { useEffect, useState } from 'react';
import { shopApi } from '../utils/shopApi';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingBag, RefreshCw } from 'lucide-react';

const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);

const STATUSES = ['pending_payment','confirmed','preparing','dispatched','en_route','delivered','received','cancelled'];

export default function ShopOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => {
    const params = filter !== 'all' ? { status: filter } : {};
    shopApi.get(`/shop/admin/orders`, { params }).then(r => setOrders(r.data)).catch(() => toast.error('Failed to load orders'));
    shopApi.get(`/shop/admin/dashboard`).then(r => setStats(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await shopApi.put(`/shop/admin/orders/${id}/status`, { status });
      toast.success(`Order updated to ${status.replace(/_/g,' ')}`);
      load();
      if (selected && selected.id === id) {
        const r = await shopApi.get(`/shop/admin/orders/${id}`);
        setSelected(r.data);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold flex items-center gap-2"><ShoppingBag className="w-7 h-7 text-primary" />Online Store Orders</h1>
          <p className="text-sm text-muted-foreground">Manage orders placed through the Bison Shop online store.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="shop-orders-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="shop-orders-stats">
          <Stat label="Total orders" value={stats.total_orders} />
          <Stat label="Pending" value={stats.pending} />
          <Stat label="Out for delivery" value={stats.out_for_delivery} />
          <Stat label="Revenue" value={`RWF ${formatRWF(stats.total_revenue)}`} />
        </div>
      )}

      <div className="flex gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[220px]" data-testid="shop-orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g,' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="shop-orders-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr><th className="text-left p-3">Order #</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Items</th><th className="text-right p-3">Total</th><th className="text-left p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No orders.</td></tr>
            ) : orders.map(o => (
              <tr key={o.id} className="border-t border-border hover:bg-muted/30" data-testid={`order-row-${o.id}`}>
                <td className="p-3 font-mono">#{o.order_number}</td>
                <td className="p-3"><div className="font-medium">{o.customer_name}</div><div className="text-xs text-muted-foreground">{o.customer_phone}</div></td>
                <td className="p-3 text-muted-foreground">{o.items.length} item(s)</td>
                <td className="p-3 text-right font-semibold">RWF {formatRWF(o.total)}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary capitalize">{o.status.replace(/_/g,' ')}</span></td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setSelected(o)} data-testid={`view-order-${o.id}`}>View</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Single dialog rendered outside the table to avoid hydration warnings */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order #{selected?.order_number}</DialogTitle>
            <DialogDescription>View order details, customer info, EBM invoice and update status.</DialogDescription>
          </DialogHeader>
          {selected && <OrderDetail order={selected} onUpdateStatus={(s) => updateStatus(selected.id, s)} onReload={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-4 bg-card rounded-xl border border-border">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold mt-1">{value ?? '–'}</div>
  </div>
);

const OrderDetail = ({ order, onUpdateStatus, onReload }) => {
  const ebmInputRef = React.useRef(null);
  const uploadEbm = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await shopApi.post(`/shop/admin/orders/${order.id}/ebm-invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('EBM invoice uploaded — customer notified');
      onReload?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
  };
  const downloadEbm = async () => {
    try {
      const r = await shopApi.get(`/shop/admin/orders/${order.id}/ebm-invoice`);
      const a = document.createElement('a');
      a.href = r.data.file;
      a.download = r.data.filename || `ebm-invoice-${order.order_number}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error('Download failed'); }
  };
  return (
  <div className="space-y-4 text-sm">
    <div>
      <div className="font-semibold mb-1">Customer{order.is_guest && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Guest</span>}</div>
      <div>{order.customer_name} · {order.customer_email} · {order.customer_phone}</div>
    </div>
    <div>
      <div className="font-semibold mb-1">Delivery</div>
      <div className="text-muted-foreground">{order.address?.label}: {order.address?.line}, {order.address?.city}</div>
      {order.address?.lat && <div className="text-xs text-muted-foreground">Pin: {order.address.lat.toFixed(5)}, {order.address.lng.toFixed(5)}</div>}
      {order.address?.notes && <div className="text-xs text-muted-foreground">Address notes: {order.address.notes}</div>}
      {order.scheduled_for && <div className="text-xs text-primary mt-1">⏰ Scheduled for: {new Date(order.scheduled_for).toLocaleString()}</div>}
      {order.delivery_zone && <div className="text-xs text-muted-foreground">Zone: {order.delivery_zone}{order.delivery_distance_km ? ` · ${order.delivery_distance_km} km` : ''}</div>}
    </div>
    {order.customer_notes && (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded">
        <div className="text-xs font-semibold text-amber-800 mb-0.5">Customer note</div>
        <div className="text-sm">{order.customer_notes}</div>
      </div>
    )}
    {order.ebm_invoice_request && (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded space-y-1" data-testid="staff-ebm-block">
        <div className="text-xs font-semibold text-blue-800 mb-1">EBM Invoice requested</div>
        <div className="text-xs"><strong>Business:</strong> {order.ebm_invoice_request.business_name}</div>
        {order.ebm_invoice_request.tin && <div className="text-xs"><strong>TIN:</strong> {order.ebm_invoice_request.tin}</div>}
        {order.ebm_invoice_request.phone && <div className="text-xs"><strong>Phone:</strong> {order.ebm_invoice_request.phone}</div>}
        {order.ebm_invoice_request.purchase_code && <div className="text-xs"><strong>Purchase code:</strong> {order.ebm_invoice_request.purchase_code}</div>}
        <div className="flex gap-2 mt-2">
          <input ref={ebmInputRef} type="file" accept="application/pdf,image/*" hidden onChange={(e) => e.target.files?.[0] && uploadEbm(e.target.files[0])} data-testid="ebm-file-input" />
          <Button size="sm" variant="outline" onClick={() => ebmInputRef.current?.click()} data-testid="ebm-upload-btn">
            {order.ebm_invoice_filename ? 'Replace EBM invoice' : 'Upload EBM invoice'}
          </Button>
          {order.ebm_invoice_filename && (
            <Button size="sm" variant="ghost" onClick={downloadEbm} data-testid="ebm-staff-download-btn">Download {order.ebm_invoice_filename}</Button>
          )}
        </div>
        {order.ebm_invoice_uploaded_at && <div className="text-[10px] text-muted-foreground">Uploaded {new Date(order.ebm_invoice_uploaded_at).toLocaleString()} by {order.ebm_invoice_uploaded_by}</div>}
      </div>
    )}
    <div>
      <div className="font-semibold mb-1">Items</div>
      <div className="space-y-1">
        {order.items.map(it => (
          <div key={it.product_id} className="flex justify-between">
            <span>{it.product_name} × {it.quantity}</span>
            <span>RWF {formatRWF(it.line_total)}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="border-t pt-2 space-y-1">
      <div className="flex justify-between"><span>Subtotal</span><span>RWF {formatRWF(order.subtotal)}</span></div>
      {order.discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-RWF {formatRWF(order.discount)}</span></div>}
      <div className="flex justify-between"><span>Delivery</span><span>RWF {formatRWF(order.delivery_fee)}</span></div>
      <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>RWF {formatRWF(order.total)}</span></div>
      <div className="text-xs text-muted-foreground mt-1">Payment: {order.payment_method.replace(/_/g,' ')}</div>
    </div>
    <div>
      <div className="font-semibold mb-2">Update status</div>
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => (
          <Button key={s} size="sm" variant={order.status === s ? 'default' : 'outline'} onClick={() => onUpdateStatus(s)} data-testid={`set-status-${s}`} className="capitalize">{s.replace(/_/g,' ')}</Button>
        ))}
      </div>
    </div>
  </div>
  );
};
